import type { CanvasesCanvas } from "@/api-client";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { canvasKeys, fetchCanvasConsoleData } from "@/hooks/useCanvasData";
import type { ConsoleLayoutItem, ConsolePanel } from "@/hooks/useCanvasData";

import { fetchCanvasVersionWithSpec } from "./lib/repository-spec-files";

export type CommittedDraftBaselines = {
  canvasSpec?: CanvasesCanvas["spec"];
  console?: {
    panels: ConsolePanel[];
    layout: ConsoleLayoutItem[];
  };
  ready: boolean;
};

type UseCommittedDraftBaselinesOptions = {
  canvasId?: string;
  versionId?: string;
  enabled: boolean;
  /** Bumps after reset/commit remounts so committed snapshots reload from the server. */
  stagingResetNonce: number;
  /** Factory apps have no Console surface — skip console.yaml so a failed read cannot block edit UI. */
  includeConsole?: boolean;
};

export function useCommittedDraftBaselines({
  canvasId,
  versionId,
  enabled,
  stagingResetNonce,
  includeConsole = true,
}: UseCommittedDraftBaselinesOptions): CommittedDraftBaselines {
  const queryClient = useQueryClient();
  const [baselines, setBaselines] = useState<CommittedDraftBaselines>({ ready: false });

  useEffect(() => {
    if (!enabled || !canvasId || !versionId) {
      setBaselines({ ready: false });
      return;
    }

    let cancelled = false;
    setBaselines({ ready: false });

    // Read the committed (stage=false) canvas and console through React Query so
    // the baselines reuse the cache the rest of the editor already populates.
    // The console read shares its key/fetcher with the draft console query, so
    // the two committed console.yaml reads are deduped into a single request.
    // Commit/discard invalidate these keys, so the nonce bump reloads fresh data.
    const versionPromise = queryClient.fetchQuery({
      queryKey: canvasKeys.versionDetail(canvasId, versionId),
      queryFn: () => fetchCanvasVersionWithSpec(canvasId, versionId),
      staleTime: Number.POSITIVE_INFINITY,
    });
    const consolePromise = includeConsole
      ? queryClient.fetchQuery({
          queryKey: canvasKeys.console(canvasId, versionId),
          queryFn: () => fetchCanvasConsoleData(canvasId, versionId, false),
          staleTime: Number.POSITIVE_INFINITY,
        })
      : Promise.resolve(undefined);

    void (async () => {
      try {
        const version = await versionPromise;
        // Console is optional for factory apps; a console failure must not block baselines.
        const consoleData = await consolePromise.catch(() => undefined);
        if (cancelled) {
          return;
        }

        setBaselines({
          canvasSpec: version?.spec,
          console: consoleData
            ? {
                panels: consoleData.panels,
                layout: consoleData.layout,
              }
            : { panels: [], layout: [] },
          ready: true,
        });
      } catch {
        if (cancelled) {
          return;
        }
        // Unblock edit bootstrap after a version-read failure. Omit canvasSpec and
        // console so local diffs stay false (hasLocalCanvasGraphDiff /
        // hasLocalConsoleDiff) until a later successful fetch — empty console
        // objects would falsely dirty Commit/Discard for apps with panels.
        setBaselines({ ready: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canvasId, enabled, includeConsole, queryClient, stagingResetNonce, versionId]);

  return baselines;
}
